/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { SkillsSlice } from '../../../../ts/state';
import { SET_ASSISTANT_ID } from '../session/actionTypes';
import { revertSnapshot } from '../snapshots/actions';
import { entityAdapter } from './adapter';
// Import all the ducks reducers
import createReducers from './ducks/create';
import deleteReducers from './ducks/delete';
import duplicateReducers from './ducks/duplicate';
import exportReducers from './ducks/export';
import getReducers from './ducks/get';
import importReducers from './ducks/import';
import listReducers from './ducks/list';
import toggleDialogReducers from './ducks/toggle-dialog';
import updateReducers from './ducks/update';
import userLabelsReducers from './ducks/user-labels';

// Initial State
const initialState = entityAdapter.getInitialState({
  listStatus: ApiStatus.IDLE, // idle, pending, fulfilled, error
  listFetched: null,
  listError: null,

  getStatus: ApiStatus.IDLE,
  getError: null,

  createStatus: ApiStatus.IDLE,
  createError: null,

  duplicateStatus: ApiStatus.IDLE,
  duplicateError: null,

  toggleDialogStatus: ApiStatus.IDLE,
  toggleDialogError: null,

  resetToggleDialogStatus: ApiStatus.IDLE,
  resetToggleDialogError: null,

  updateStatus: ApiStatus.IDLE,
  updateError: null,

  deleteStatus: ApiStatus.IDLE,
  deleteError: null,

  importStatus: ApiStatus.IDLE,
  importError: null,
  importProgress: 0,

  exportStatus: ApiStatus.IDLE,
  exportError: null,

  userLabelsStatus: ApiStatus.IDLE,
  userLabelsError: null,
  userLabels: {},

  training: {},
  trainingError: {},
  trainingStatus: {},

  testing: {},
  testingError: {},
  testingStatus: ApiStatus.IDLE
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): SkillsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'skills',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    createReducers(builder);
    duplicateReducers(builder);
    toggleDialogReducers(builder);
    exportReducers(builder);
    importReducers(builder);
    listReducers(builder);
    deleteReducers(builder);
    updateReducers(builder);
    getReducers(builder);
    userLabelsReducers(builder);

    builder
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (state, action) => {
        // Clear the cache if the assistant changes
        if (action.assistantId) {
          return computeState();
        }
      })
      .addCase(revertSnapshot.fulfilled, (state, action) => {
        entityAdapter.upsertOne(state, action.payload);
      });
  }
});

export default slice.reducer;
